import { MutableRefObject, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';

import {
  Field,
  Form,
  FormTheme,
  Widget,
  useForm,
} from '@kanda-libs/ks-component-ts';
import {
  Button,
  Loader,
  ModalContainer,
  ModalLayoutCenter,
  ModalsWrapperContext,
  StringIndexedObject,
  useToast,
} from '@kanda-libs/ks-design-library';
import {
  Company,
  CompanyTraining,
  actions,
} from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/shared/App';
import useApiError from 'hooks/useApiError';
import { selectors } from 'store';

import { MODAL_ID, OPTIONS, VALIDATION } from './constants';

interface TrainingStatusFormValues {
  company: {
    training_status: CompanyTraining;
  };
}

const TrainingStatusModal: React.FunctionComponent = function () {
  const dispatch = useAppDispatch();
  const form = useForm<TrainingStatusFormValues>({
    mode: 'onBlur',
  });
  const { hideModal } = useContext(ModalsWrapperContext);

  const company = useSelector(selectors.company.getEntity);
  const isSubmitting = useSelector(selectors.company.getIsSubmitting);

  const onApiError = useApiError('Unknown error updating company training');

  const { showSuccess } = useToast();

  const onSubmit = useCallback(
    (formValues: StringIndexedObject<TrainingStatusFormValues>) => {
      const fv = formValues as unknown as TrainingStatusFormValues;
      if (!company) return;
      const id = company?.cid;
      if (!id) return;
      dispatch(
        actions.putCompany({
          body: {
            ...company,
            training_status: fv.company.training_status,
          },
          params: {
            id,
          },
          onError: (err) => {
            onApiError(err);
            hideModal(MODAL_ID);
          },
          onSuccess: () => {
            showSuccess('Company training updated');
            hideModal(MODAL_ID);
          },
        }),
      );
    },
    [company, dispatch, onApiError, showSuccess],
  );

  return (
    <>
      <ModalContainer id={MODAL_ID} opacity={60}>
        {({ id, handleClose, ref }) => (
          <ModalLayoutCenter
            id={id}
            onClose={handleClose}
            ref={ref as MutableRefObject<HTMLDivElement>}
            className="w-10/12 md:w-120"
          >
            <Form
              form={form}
              onSubmit={onSubmit}
              id="staff-company-training-status"
            >
              <div className="flex flex-col">
                <h1 className="text-16-24-em mb-4">
                  Update Company Training Status
                </h1>
                <h3 className="text-14-22 text-neutral-700 mb-5">
                  Select a new training status for this company
                </h3>
                <FormTheme variant="streamline">
                  <Field.Validator validation={VALIDATION}>
                    <Field.Select
                      options={OPTIONS}
                      name="company.training_status"
                      placeholder="Select..."
                    />
                  </Field.Validator>
                </FormTheme>
                <div className="grid grid-cols-2 gap-x-6 mt-3">
                  <Button.Text
                    id="staff-qa-lender-config-modal-no"
                    label="No"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    variant="outline"
                  />
                  <Button.Text
                    id="staff-qa-lender-config-modal-yes"
                    label="Yes"
                    submit
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </Form>
          </ModalLayoutCenter>
        )}
      </ModalContainer>
      <Loader isLoading={isSubmitting} />
    </>
  );
};

export default TrainingStatusModal;
